/**
 * The structural operations of the Cuts station, on the VERSIONED recut engine.
 *
 * All of them follow "never destroy, always create": inputs become obsolete
 * (stamps kept, cached audio dropped), fresh records are minted with the
 * user's naming rules, and the active records always tile the recording.
 *
 *   moveEdge         — drag: the boundary between two clips moves; audio
 *                      transfers, WORDS DO NOT (drag fixes sound geometry only)
 *   splitClip        — one clip becomes A1.x.1 + A1.x.2 at a tapped point.
 *                      NOT CURRENTLY WIRED to any control; `recut.nextNames`'
 *                      nested-name rule exists to serve it and would be
 *                      unexplainable without it.
 *   joinClip         — two neighbours become the next top number (A1.1+A1.2 → A1.3)
 *   setClipBounds    — the modal editor's save, as up to two `moveEdge` steps
 *   takeWordAcross   — take a word from the nearest word-BEARING clip, looking
 *                      past silence in between; boundaries do not move
 *   transferEdgeWord — move one edge word, id and audio, across a boundary
 *   convertToSilence — the versioned alternative to deleting a clip.
 *                      NOT WIRED: the live delete path is `db.removeSegment`,
 *                      which hard-deletes. The "never destroy" promise in this
 *                      header and in `recut.ts`'s is therefore aspirational
 *                      wherever CorrectPage is concerned. See F2.
 *
 * Any neighbour a human already judged is a hard wall for every one of them.
 *
 * THE OTHER ENGINE: `services/boundaryEdits.ts` edits the partition IN PLACE
 * (no new records, no audit trail). Its header explains when to reach for
 * which. They are deliberately not merged.
 */

import { getSegment, getSegmentsByTask, getTask } from '../db';
import { isFillerSpelling } from '../lib/labelText';
import { activeLeaves, type Segment, type TaskStatus, type Word } from '../types';
import { sliceToWav } from './audioSlicer';
import { decodedAudioFor } from './decodedAudio';
import { applyRecut, wordsOf, type RecutResult } from './recut';

/** A clip may never become smaller than this through any cut operation. */
const MIN_CLIP_SEC = 0.08;

/** The shared single-slot decode, with this engine's own failure wording. */
const bufferFor = (taskId: string): Promise<AudioBuffer> =>
  decodedAudioFor(taskId, 'The original audio for this recording is missing.');

/** This task's live leaves in time order — the set every cut operates on. */
async function loadActiveLeaves(taskId: string): Promise<Segment[]> {
  return activeLeaves(await getSegmentsByTask(taskId));
}

function judged(s: Segment): boolean {
  return s.verify !== 'pending';
}

export interface CutOpResult {
  /** Fresh records, in time order. The page swaps them into its queue. */
  created: Segment[];
  /** Ids of records that became obsolete (page removes them from its queue). */
  obsoletedIds: string[];
  status?: TaskStatus;
}

function toResult(r: RecutResult): CutOpResult {
  return {
    created: [...r.created].sort((a, b) => a.startSec - b.startSec),
    obsoletedIds: r.obsoleted.map((s) => s.id),
    status: r.status,
  };
}

/**
 * Drag released: the clip's `side` boundary lands at `newSec` (absolute).
 * The neighbouring record gives or takes the difference — audio only, each
 * side keeps its own words.
 *
 * Module-private: `setClipBounds` is the only caller and there has never been
 * one outside this file. A drag from the UI goes through the page, which calls
 * `setClipBounds`.
 */
async function moveEdge(
  segmentId: string,
  side: 'start' | 'end',
  newSec: number,
): Promise<CutOpResult> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That clip is no longer there.');
  const leaves = await loadActiveLeaves(seg.taskId);
  const at = leaves.findIndex((s) => s.id === seg.id);
  if (at < 0) throw new Error('That clip is no longer active.');
  const neighbour = side === 'start' ? leaves[at - 1] : leaves[at + 1];
  const buffer = await bufferFor(seg.taskId);

  // Clamp: this clip and its neighbour both keep at least MIN_CLIP_SEC.
  const lo = side === 'start' ? (neighbour ? neighbour.startSec + MIN_CLIP_SEC : 0) : seg.startSec + MIN_CLIP_SEC;
  const hi =
    side === 'start'
      ? seg.endSec - MIN_CLIP_SEC
      : neighbour
        ? neighbour.endSec - MIN_CLIP_SEC
        : buffer.duration;
  const target = Math.min(Math.max(newSec, lo), hi);
  const oldEdge = side === 'start' ? seg.startSec : seg.endSec;
  if (Math.abs(target - oldEdge) < 0.01) {
    return { created: [], obsoletedIds: [] };
  }
  if (neighbour && judged(neighbour)) {
    throw new Error(`“${neighbour.heard}” was already judged — it cannot be changed from here.`);
  }

  const specOf = (s: Segment, startSec: number, endSec: number) => ({
    startSec,
    endSec,
    words: wordsOf(s),
    kind: s.kind,
    confidence: s.confidence,
  });

  if (!neighbour) {
    // At the recording's very edge there is no neighbour to transfer with —
    // the clip simply claims (or releases) up to the file boundary.
    const r = await applyRecut(seg.taskId, buffer, [seg], [
      specOf(seg, side === 'start' ? target : seg.startSec, side === 'start' ? seg.endSec : target),
    ]);
    return toResult(r);
  }

  const r =
    side === 'start'
      ? await applyRecut(seg.taskId, buffer, [neighbour, seg], [
          specOf(neighbour, neighbour.startSec, target),
          specOf(seg, target, seg.endSec),
        ])
      : await applyRecut(seg.taskId, buffer, [seg, neighbour], [
          specOf(seg, seg.startSec, target),
          specOf(neighbour, target, neighbour.endSec),
        ]);
  return toResult(r);
}

/**
 * Split at a tapped point: A1.1 → A1.1.1 + A1.1.2 (nested names).
 * Words go to the side the aligner measures them on when the helper is up;
 * otherwise they stay with the larger side and the other side is flagged
 * `unclear` so a human types nothing but still re-checks it.
 */
export async function splitClip(segmentId: string, atSec: number): Promise<CutOpResult> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That clip is no longer there.');
  const buffer = await bufferFor(seg.taskId);
  const point = Math.min(Math.max(atSec, seg.startSec + MIN_CLIP_SEC), seg.endSec - MIN_CLIP_SEC);

  const words = wordsOf(seg);
  let leftWords: Word[] = [];
  let rightWords: Word[] = [];
  if (words.length > 0) {
    const { isAlignerAvailable, alignTranscript } = await import('./aligner');
    let placed = false;
    if (await isAlignerAvailable()) {
      try {
        const task = await getTask(seg.taskId);
        const wav = sliceToWav(buffer, seg.startSec, seg.endSec, { padSec: 0 });
        const aligned = await alignTranscript({
          wav,
          transcript: words.map((w) => w.text).join(' '),
          language: task?.language ?? 'ara',
        });
        if (aligned.length === words.length) {
          const rel = point - seg.startSec;
          words.forEach((w, i) => {
            const mid = (aligned[i].start + aligned[i].end) / 2;
            (mid <= rel ? leftWords : rightWords).push(w);
          });
          placed = true;
        }
      } catch {
        // fall through to the size rule
      }
    }
    if (!placed) {
      const leftLen = point - seg.startSec;
      const rightLen = seg.endSec - point;
      if (leftLen >= rightLen) leftWords = words;
      else rightWords = words;
    }
  }

  const sideKind = (w: Word[]): Segment['kind'] =>
    w.length > 0 ? (seg.kind === 'silence' ? 'word' : seg.kind) : seg.kind === 'silence' ? 'silence' : 'unclear';

  const r = await applyRecut(
    seg.taskId,
    buffer,
    [seg],
    [
      { startSec: seg.startSec, endSec: point, words: leftWords, kind: sideKind(leftWords), confidence: seg.confidence },
      { startSec: point, endSec: seg.endSec, words: rightWords, kind: sideKind(rightWords), confidence: seg.confidence },
    ],
    { nameBase: seg.name },
  );
  return toResult(r);
}

/** Join with a neighbour: A1.1 + A1.2 → A1.3 (next top number). */
export async function joinClip(segmentId: string, direction: 'prev' | 'next'): Promise<CutOpResult> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That clip is no longer there.');
  const leaves = await loadActiveLeaves(seg.taskId);
  const at = leaves.findIndex((s) => s.id === seg.id);
  const other = direction === 'prev' ? leaves[at - 1] : leaves[at + 1];
  if (!other) throw new Error('There is no clip on that side.');
  if (judged(other)) throw new Error(`“${other.heard}” was already judged — it cannot be joined from here.`);

  const [a, b] = seg.startSec < other.startSec ? [seg, other] : [other, seg];
  const words = [...wordsOf(a), ...wordsOf(b)];
  const kind =
    a.kind === b.kind ? a.kind : words.length > 0 ? 'word' : 'unclear';
  const buffer = await bufferFor(seg.taskId);
  const r = await applyRecut(seg.taskId, buffer, [a, b], [
    {
      startSec: a.startSec,
      endSec: b.endSec,
      words,
      kind,
      confidence: Math.min(a.confidence, b.confidence),
    },
  ]);
  return toResult(r);
}

/**
 * Set both boundaries of a clip in one go (the modal editor's save).
 *
 * Runs as up to two {@link moveEdge} steps so every displaced millisecond is
 * re-homed to a neighbour by the same rules — and because each step mints new
 * records, the second step is applied to the record the first one produced.
 */
export async function setClipBounds(
  segmentId: string,
  newStart: number,
  newEnd: number,
): Promise<CutOpResult> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That clip is no longer there.');

  let workingId = segmentId;
  const obsoletedIds: string[] = [];
  let created: Segment[] = [];

  if (Math.abs(newStart - seg.startSec) > 0.01) {
    const r = await moveEdge(workingId, 'start', newStart);
    obsoletedIds.push(...r.obsoletedIds);
    created = r.created;
    const mid = (Math.max(newStart, 0) + seg.endSec) / 2;
    const self = r.created.find((c) => c.startSec <= mid && c.endSec >= mid);
    if (self) workingId = self.id;
  }

  if (Math.abs(newEnd - seg.endSec) > 0.01) {
    const r2 = await moveEdge(workingId, 'end', newEnd);
    obsoletedIds.push(...r2.obsoletedIds);
    created = [...created.filter((c) => !r2.obsoletedIds.includes(c.id)), ...r2.created];
  }

  return {
    created: created.sort((a, b) => a.startSec - b.startSec),
    obsoletedIds,
  };
}

/**
 * Take a word from the nearest word-bearing clip on one side, LOOKING PAST
 * any silence/noise clips in between.
 *
 * The case this exists for, from real use: clip 1 is "مرحبا" but the audio is
 * "مرحبا ايه"; clip 2 is silence; clip 3 is written "ايه انا اسمي امجد" but
 * sounds like "انا اسمي امجد". The stray "ايه" belongs to clip 1 — and clips
 * 1 and 3 are not neighbours, so no neighbour-only operation could ever fix
 * it. Here the silence in between is simply stepped over.
 *
 * Boundaries do NOT move: the human is saying "I hear that word in THIS
 * clip", which means the audio is already here and only the label was in the
 * wrong place. The clips in between are passed through untouched (they are
 * inputs only so the recut covers one contiguous span).
 */
export async function takeWordAcross(
  segmentId: string,
  direction: 'prev' | 'next',
): Promise<CutOpResult> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That clip is no longer there.');
  const leaves = await loadActiveLeaves(seg.taskId);
  const at = leaves.findIndex((s) => s.id === seg.id);
  if (at < 0) throw new Error('That clip is no longer active.');

  const step = direction === 'prev' ? -1 : 1;
  let j = at + step;
  while (j >= 0 && j < leaves.length && wordsOf(leaves[j]).length === 0) j += step;
  if (j < 0 || j >= leaves.length) throw new Error('There is no word on that side to take.');
  const donor = leaves[j];
  if (judged(donor)) throw new Error(`“${donor.heard}” was already judged — it cannot be changed from here.`);

  const donorWords = wordsOf(donor);
  const moved = direction === 'prev' ? donorWords[donorWords.length - 1] : donorWords[0];
  const remaining = donorWords.filter((w) => w.id !== moved.id);
  const mine = wordsOf(seg);
  const newMine = direction === 'prev' ? [moved, ...mine] : [...mine, moved];

  const looksFiller = isFillerSpelling(moved.text);
  const myKind: Segment['kind'] =
    seg.kind === 'silence' || seg.kind === 'unclear'
      ? newMine.length === 1 && looksFiller
        ? 'filler'
        : 'word'
      : seg.kind;

  const lo = Math.min(at, j);
  const hi = Math.max(at, j);
  const inputs = leaves.slice(lo, hi + 1);
  const buffer = await bufferFor(seg.taskId);
  const outputs = inputs.map((s) => {
    if (s.id === seg.id) {
      return { startSec: s.startSec, endSec: s.endSec, words: newMine, kind: myKind, confidence: s.confidence };
    }
    if (s.id === donor.id) {
      return {
        startSec: s.startSec,
        endSec: s.endSec,
        words: remaining,
        kind: remaining.length === 0 ? ('silence' as const) : s.kind,
        confidence: s.confidence,
      };
    }
    // untouched pass-through, so the recut spans one contiguous stretch
    return { startSec: s.startSec, endSec: s.endSec, words: wordsOf(s), kind: s.kind, confidence: s.confidence };
  });

  const r = await applyRecut(seg.taskId, buffer, inputs, outputs);
  return toResult(r);
}

/* ------------------------------------------------------------------ */
/* Edge-word transfer — versioned, list-driven                         */
/* ------------------------------------------------------------------ */
/*
 * Moved here from `segmentEdits.ts` in the restructure. It belongs with the
 * other Cuts operations: same recut engine, same page, same "never destroy"
 * semantics. `segmentEdits.ts` is the in-place MERGE engine and had no claim
 * on it beyond history.
 */

/**
 * Move one edge word — the WORD ENTITY, id and all, together with its
 * measured audio — across the boundary between two adjacent clips.
 *
 * This is a RECUT: both records become obsolete (stamps kept, cache audio
 * dropped) and two fresh records are minted with new names and made-from
 * links. Nothing is destroyed; the audit trail shows the word's journey.
 */
export type EdgeWordOp = 'give-first' | 'give-last' | 'take-prev' | 'take-next';

export interface EdgeWordOutcome {
  updated: Segment;
  counterpart: Segment;
  obsoletedIds: string[];
  movedWord: string;
  status?: TaskStatus;
}

export async function transferEdgeWord(segmentId: string, op: EdgeWordOp): Promise<EdgeWordOutcome> {
  const { alignTranscript, isAlignerAvailable } = await import('./aligner');
  const { getTask } = await import('../db');

  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That segment is no longer there.');
  const siblings = activeLeaves(await getSegmentsByTask(seg.taskId));
  const pos = siblings.findIndex((s) => s.id === seg.id);
  const neighbour = op === 'give-first' || op === 'take-prev' ? siblings[pos - 1] : siblings[pos + 1];
  if (!neighbour) throw new Error('There is no clip on that side.');
  if (neighbour.verify !== 'pending') {
    throw new Error(`“${neighbour.heard}” was already judged — send it back to Cuts first.`);
  }

  const giving = op === 'give-first' || op === 'give-last';
  const source = giving ? seg : neighbour;
  const receiver = giving ? neighbour : seg;
  const edge: 'first' | 'last' =
    op === 'give-first' ? 'first' : op === 'give-last' ? 'last' : op === 'take-prev' ? 'last' : 'first';

  const sourceWords = wordsOf(source);
  if (sourceWords.length === 0) {
    throw new Error(`“${source.heard}” has no words to move — use the ±0.5s buttons instead.`);
  }
  const moved: Word = edge === 'first' ? sourceWords[0] : sourceWords[sourceWords.length - 1];

  if (!(await isAlignerAvailable())) {
    throw new Error('The alignment helper is not running, so the word cannot be measured. Use ±0.5s instead.');
  }
  const task = await getTask(seg.taskId);
  const buffer = await bufferFor(seg.taskId);
  const wav = sliceToWav(buffer, source.startSec, source.endSec, { padSec: 0 });
  const aligned = await alignTranscript({
    wav,
    transcript: sourceWords.map((w) => w.text).join(' '),
    language: task?.language ?? 'ara',
  });
  if (aligned.length !== sourceWords.length) {
    throw new Error('The aligner could not place the words — use the ±0.5s buttons instead.');
  }

  const aw = edge === 'first' ? aligned[0] : aligned[aligned.length - 1];
  const rel = edge === 'first' ? aw.end : aw.start;
  const sourceIsPrev = source.startSec < receiver.startSec;
  const remaining = sourceWords.filter((w) => w.id !== moved.id);
  let boundary = source.startSec + Math.min(Math.max(rel, 0.02), source.endSec - source.startSec - 0.02);
  if (remaining.length === 0) boundary = sourceIsPrev ? source.startSec : source.endSec;

  const receiverWords = wordsOf(receiver);
  const newReceiverWords: Word[] = sourceIsPrev ? [moved, ...receiverWords] : [...receiverWords, moved];
  const looksFiller = newReceiverWords.length === 1 && isFillerSpelling(moved.text);

  const inputs = [source, receiver];
  const outputs = [];
  const sourceSpan: [number, number] = sourceIsPrev ? [source.startSec, boundary] : [boundary, source.endSec];
  const receiverSpan: [number, number] = sourceIsPrev
    ? [boundary, receiver.endSec]
    : [receiver.startSec, boundary];
  if (sourceSpan[1] - sourceSpan[0] >= 0.08) {
    outputs.push({
      startSec: sourceSpan[0],
      endSec: sourceSpan[1],
      words: remaining,
      kind: remaining.length === 0 ? ('silence' as const) : source.kind,
      confidence: source.confidence,
    });
  } else {
    // nothing meaningful left — the receiver takes the whole source span
    if (sourceIsPrev) receiverSpan[0] = source.startSec;
    else receiverSpan[1] = source.endSec;
  }
  outputs.push({
    startSec: receiverSpan[0],
    endSec: receiverSpan[1],
    words: newReceiverWords,
    kind:
      receiver.kind === 'silence' || receiver.kind === 'unclear'
        ? looksFiller
          ? ('filler' as const)
          : ('word' as const)
        : receiver.kind,
    confidence: Math.min(source.confidence, receiver.confidence),
  });

  const result = await applyRecut(seg.taskId, buffer, inputs, outputs);
  const createdReceiver = result.created.find((c) =>
    c.words?.some((w) => w.id === moved.id),
  ) ?? result.created[result.created.length - 1];
  const createdSource = result.created.find((c) => c.id !== createdReceiver.id) ?? createdReceiver;
  return {
    updated: giving ? createdSource : createdReceiver,
    counterpart: giving ? createdReceiver : createdSource,
    obsoletedIds: result.obsoleted.map((s) => s.id),
    movedWord: moved.text,
    status: result.status,
  };
}

/**
 * "Nothing is here" — the versioned replacement for deleting a clip. The
 * record becomes obsolete and a silence record takes its exact span, so the
 * timeline stays fully labeled and the removal is auditable and reversible.
 */
export async function convertToSilence(segmentId: string): Promise<{ created: Segment; status?: TaskStatus }> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That segment is no longer there.');
  const buffer = await bufferFor(seg.taskId);
  const result = await applyRecut(seg.taskId, buffer, [seg], [
    {
      startSec: seg.startSec,
      endSec: seg.endSec,
      words: [],
      kind: 'silence',
      confidence: 0.8,
    },
  ]);
  return { created: result.created[0], status: result.status };
}
